import { FormEvent, useState } from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Send from '@mui/icons-material/Send';

interface SupportAgentComposerProps {
  isSending?: boolean;
  onSend: (content: string) => void | Promise<void>;
}

export default function SupportAgentComposer({
  isSending = false,
  onSend,
}: SupportAgentComposerProps) {
  const [draft, setDraft] = useState('');

  const handleSubmit = (event?: FormEvent) => {
    event?.preventDefault();

    const content = draft.trim();
    if (!content || isSending) {
      return;
    }

    setDraft('');
    void onSend(content);
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        display: 'flex',
        gap: 1,
        alignItems: 'flex-end',
        width: '100%',
        bgcolor: 'transparent',
      }}
    >
      <TextField
        fullWidth
        size="small"
        multiline
        maxRows={4}
        placeholder="Enter your message"
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            handleSubmit();
          }
        }}
        autoFocus
        slotProps={{
          htmlInput: { 'data-testid': 'support-agent-input' },
        }}
        sx={{
          flex: 1,
          minWidth: 0,
          '& .MuiOutlinedInput-root': {
            bgcolor: (theme) =>
              theme.palette.mode === 'dark'
                ? 'transparent'
                : theme.palette.semantic.surface.bg1,
            color: (theme) => theme.palette.semantic.text.body,
            borderRadius: '4px',
            '& fieldset': {
              borderColor: (theme) =>
                theme.palette.mode === 'dark'
                  ? theme.palette.colors.white
                  : theme.palette.semantic.outline.light,
            },
            '&:hover fieldset': {
              borderColor: (theme) =>
                theme.palette.mode === 'dark'
                  ? theme.palette.colors.white
                  : theme.palette.semantic.outline.medium,
            },
            '&.Mui-focused fieldset': {
              borderColor: (theme) =>
                theme.palette.mode === 'dark'
                  ? theme.palette.colors.info[400]
                  : theme.palette.semantic.outline.primary,
            },
          },
          '& .MuiInputBase-input::placeholder': {
            color: (theme) => theme.palette.semantic.text.secondary,
            opacity: 1,
          },
        }}
      />
      <IconButton
        type="submit"
        aria-label="Send message"
        data-testid="support-agent-send"
        disabled={!draft.trim() || isSending}
        sx={{
          flexShrink: 0,
          color: (theme) => theme.palette.colors.accent[500],
          '&.Mui-disabled': {
            color: (theme) => theme.palette.semantic.icon.disabled,
          },
        }}
      >
        {isSending ? (
          <CircularProgress
            size={18}
            sx={{
              color: (theme) => theme.palette.colors.accent[500],
            }}
          />
        ) : (
          <Send sx={{ transform: 'rotate(-45deg)' }} />
        )}
      </IconButton>
    </Box>
  );
}
